import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { BaseError } from '@utils/types/shared/base';
import { formatDuration } from '@utils/date';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { GET_URL_NEW } from '@utils/api/endpoints';

interface UsageParams {
  period_start: string;
  period_end: string;
}

export interface UsageBo {
  organization_id: number;
  organization_name: string;
  stt_units: number;
  alignment_units: number;
  translation_characters: number;
  auto_minute_exports: number;
  custom_models_hosted: number;
  custom_models_iterations: number;
  total_cost: string;
}

export interface UsageBoResponse {
  total_results: number;
  result: UsageBo[];
}

export const useGetUsageBo = (params: UsageParams) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sort = (searchParams.sort as string | undefined) || 'total_cost';
  const direction = searchParams.direction || 'desc';

  const fetchUsage = async (): Promise<UsageBoResponse> => {
    const { usageWith } = GET_URL_NEW({
      offset,
      limit,
      sort,
      direction
    });

    const { data } = await Axios.get<UsageBoResponse>(usageWith(), {
      params: {
        splits: ['organization', 'month'],
        period_start: params.period_start,
        period_end: params.period_end
      },
      paramsSerializer: p => {
        const u = new URLSearchParams();
        Object.entries(p).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach(x => u.append(k, String(x)));
          else if (v !== undefined && v !== null) u.append(k, String(v));
        });
        return u.toString();
      }
    });

    return data;
  };

  return useQuery<
    UsageBoResponse,
    AxiosError<BaseError>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { rows: any[]; total: number; result: UsageBo[] }
  >(
    [
      'usage',
      params.period_start,
      params.period_end,
      direction,
      sort,
      offset,
      limit
    ],
    fetchUsage,
    {
      enabled: !!params.period_start && !!params.period_end,
      keepPreviousData: true,
      staleTime: 15_000,
      select: raw => ({
        result: raw.result,
        total: raw.total_results,
        rows: raw.result.map(item => ({
          id: item.organization_id,
          organization_id: item.organization_id,
          organization_name: item.organization_name ?? '-',
          stt_units: formatDuration(item.stt_units),
          alignment_units: formatDuration(item.alignment_units),
          translation_characters:
            item.translation_characters != null
              ? Number(item.translation_characters).toLocaleString()
              : '-',
          auto_minute_exports:
            item.auto_minute_exports != null ? item.auto_minute_exports : '-',
          custom_models_hosted:
            item.custom_models_hosted != null ? item.custom_models_hosted : '-',
          custom_models_iterations:
            item.custom_models_iterations != null
              ? item.custom_models_iterations
              : '-',
          total_cost:
            item.total_cost != null ? Number(item.total_cost).toFixed(2) : '-'
        }))
      }),
      onError: error => {
        const er = error.response?.data as BaseError | undefined;
        const msg =
          er?.error_description ||
          er?.detail ||
          er?.error ||
          er?.message ||
          t('an-error-occurred-while-fetching-usage');
        toast.error(msg);
      }
    }
  );
};
